import { Extension } from '@tiptap/core';
import { Editor } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Node, Mark, Fragment, Slice } from '@tiptap/pm/model';
import { liftTarget } from '@tiptap/pm/transform';
import { NodeSelection } from '@tiptap/pm/state';
import { syncThreadsWithProvider } from './utils';

import { CommentsOptions } from './types';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comments: {
      /**
       * Create a new pending thread
       */
      createPendingThread: () => ReturnType;
      /**
       * Create a new thread with an optional initial comment
       */
      setThread: (
        threadId: string,
        options: {
          /** Initial comment content */
          content?: string;
          /** Thread metadata */
          data?: Record<string, unknown>;
          /** Comment metadata */
          commentData?: Record<string, unknown>;
        }
      ) => ReturnType;

      /**
       * Remove a thread from the document
       * @param threadId - The thread ID to remove. If not provided, removes thread at current selection
       * @param options - Additional options
       */
      removeThread: (
        threadId?: string,
        options?: {
          /** Also delete the thread from the provider */
          deleteThread?: boolean;
        }
      ) => ReturnType;

      /**
       * Select a thread and optionally focus on it
       */
      selectThread: (options: {
        /** Thread ID to select. Pass null to clear selection */
        id: string | null;
        /** Whether to move cursor focus to the thread */
        focus?: boolean;
        /** Whether to scroll the thread into view */
        scrollIntoView?: boolean;
      }) => ReturnType;

      /**
       * Mark a thread as resolved
       * @param threadId - The thread ID to resolve
       */
      resolveThread: (threadId: string) => ReturnType;

      /**
       * Mark a thread as unresolved
       * @param threadId - The thread ID to unresolve
       */
      unresolveThread: (threadId: string) => ReturnType;

      /**
       * Update thread metadata
       * @param threadId - The thread ID to update
       * @param data - New metadata to merge with existing data
       */
      updateThread: (
        threadId: string,
        data: Record<string, unknown>
      ) => ReturnType;

      /**
       * Add a comment to an existing thread
       */
      createComment: (options: {
        /** The thread ID to add comment to */
        threadId: string;
        /** Comment content */
        content: string;
        /** Optional comment metadata */
        data?: Record<string, unknown>;
      }) => ReturnType;

      /**
       * Update an existing comment
       */
      updateComment: (options: {
        /** The thread ID containing the comment */
        threadId: string;
        /** The comment ID to update */
        commentId: string;
        /** New comment content */
        content?: string;
        /** New comment metadata */
        data?: Record<string, unknown>;
      }) => ReturnType;

      /**
       * Remove a comment from a thread
       */
      removeComment: (options: {
        /** The thread ID containing the comment */
        threadId: string;
        /** The comment ID to remove */
        commentId: string;
        /** Delete the thread if this was the last comment */
        deleteThread?: boolean;
      }) => ReturnType;

      /**
       * Manually synchronize threads between document and provider
       */
      syncThreads: () => ReturnType;
    };
  }
}

export const Comments = Extension.create<CommentsOptions>({
  name: 'comments',

  addOptions() {
    return {
      provider: null,
      blockThreadTypeName: 'blockThread',
      inlineThreadTypeName: 'inlineThread',
      onClickThread: undefined,
      deleteUnreferencedThreads: true,
      classes: {
        thread: 'comment-thread',
        block: 'comment-thread-block',
        inline: 'comment-thread-inline',
        hover: 'comment-thread-hovered',
        selected: 'comment-thread-selected',
        resolved: 'comment-thread-resolved',
        unresolved: 'comment-thread-unresolved',
      },
    };
  },

  addStorage() {
    return {
      isPendingThread: false,
      focusedThreads: [] as string[],
      debounceTimer: undefined as number | undefined,
    };
  },

  addCommands() {
    return {
      createPendingThread:
        () =>
        ({ tr, state, chain, editor }) => {
          const { selection } = state;
          const isNodeSelection = selection instanceof NodeSelection;
          this.storage.isPendingThread = true;

          tr.setMeta('pendingThread', {
            from: selection.from,
            to: selection.to,
            isNodeSelection,
          });

          this.options.provider?.createPendingThread({ editor });

          chain().blur().run();

          return true;
        },
      setThread:
        (threadId, { data }) =>
        ({ chain, state, tr }) => {
          if (this.options.provider === null) {
            return false;
          }

          const { selection } = state;
          const isNodeSelection = selection instanceof NodeSelection;

          if (!threadId) {
            return false;
          }

          // Handle node selection
          if (isNodeSelection) {
            return chain()
              .setNodeSelection(selection.from)
              .wrapIn(this.options.blockThreadTypeName, {
                id: threadId,
              })
              .setTextSelection(0) // hack to unselect text
              .run();
          }

          state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            if (
              node.type.isBlock &&
              !node.isTextblock &&
              node.childCount === 0
            ) {
              chain().command((commandContext) => {
                const { state, dispatch } = commandContext;
                const blockThreadNode =
                  state.schema.nodes[this.options.blockThreadTypeName];

                if (!blockThreadNode) {
                  return false;
                }

                const newNode = blockThreadNode.create(
                  {
                    id: threadId,
                  },
                  node
                );

                tr.replaceWith(pos, pos + 1, newNode);
                dispatch && dispatch(tr);
                return true;
              });
            }
          });

          // Add inline thread mark
          return chain()
            .focus()
            .setTextSelection({
              from: selection.from,
              to: selection.to,
            })
            .setMark(this.options.inlineThreadTypeName, {
              id: threadId,
            })
            .setTextSelection(0) // hack to unselect text
            .run();
        },
      removeThread:
        (threadId?: string, options?: { deleteThread?: boolean }) =>
        ({ tr, state }) => {
          let targetThreadId = threadId;

          if (!targetThreadId) {
            const { selection } = state;
            const { $from, $to } = selection;

            const marks = $from.marksAcross($to);
            marks?.forEach((mark) => {
              if (
                mark.type.name === this.options.inlineThreadTypeName &&
                !targetThreadId
              ) {
                targetThreadId = mark.attrs.id;
              }
            });

            if (!targetThreadId) {
              state.doc.nodesBetween(selection.from, selection.to, (node) => {
                if (
                  node.type.name === this.options.blockThreadTypeName &&
                  !targetThreadId
                ) {
                  targetThreadId = node.attrs.id;
                }
              });
            }
          }

          if (!targetThreadId) return false;

          const inlineRemovals: Array<{
            from: number;
            to: number;
            mark: Mark;
          }> = [];
          const blockRemovals: Array<{ from: number; to: number }> = [];

          state.doc.descendants((node, pos) => {
            node.marks.forEach((mark) => {
              if (mark.type.name === this.options.inlineThreadTypeName) {
                const markThreadId = mark.attrs.id;
                if (markThreadId === targetThreadId) {
                  inlineRemovals.push({
                    from: pos,
                    to: pos + node.nodeSize,
                    mark,
                  });
                }
              }
            });

            if (node.type.name === this.options.blockThreadTypeName) {
              const nodeThreadId = node.attrs.id;
              if (nodeThreadId === targetThreadId) {
                blockRemovals.push({
                  from: pos,
                  to: pos + node.nodeSize,
                });
              }
            }
          });

          inlineRemovals.forEach(({ from, to }) => {
            const mappedFrom = tr.mapping.map(from);
            const mappedTo = tr.mapping.map(to);
            tr.removeMark(
              mappedFrom,
              mappedTo,
              state.schema.marks[this.options.inlineThreadTypeName]
            );
          });

          blockRemovals.sort((a, b) => b.from - a.from);

          blockRemovals.forEach(({ from }) => {
            const mappedPos = tr.mapping.map(from);
            const $pos = tr.doc.resolve(mappedPos);

            for (let i = $pos.depth; i > 0; i--) {
              const node = $pos.node(i);
              if (node.type.name === this.options.blockThreadTypeName) {
                const $from = tr.doc.resolve($pos.before(i));
                const $to = tr.doc.resolve($pos.after(i));
                const range = $from.blockRange($to);

                if (range && liftTarget(range) !== null) {
                  tr.lift(range, liftTarget(range)!);
                }
                break;
              }
            }
          });

          if (options?.deleteThread && this.options.provider) {
            this.options.provider.deleteThread(targetThreadId);
          }

          tr.setMeta('threadUpdate', true);

          return true;
        },
      selectThread:
        ({ id, focus = true, scrollIntoView = false }) =>
        ({ chain, state }) => {
          const commands = chain();

          if (id) {
            commands.command(({ tr }) => {
              tr.setMeta('threadSelect', id);
              return true;
            });
          } else {
            commands.command(({ tr }) => {
              tr.setMeta('threadSelect', false);
              return true;
            });
          }

          if (id && focus) {
            let found = false;

            state.doc.descendants((node: Node, pos: number) => {
              if (found) return false;

              if (node.type.name === this.options.blockThreadTypeName) {
                const nodeThreadId = node.attrs.id;
                if (nodeThreadId === id) {
                  commands.setTextSelection(pos + 1);
                  if (scrollIntoView) commands.scrollIntoView();
                  found = true;
                  return false;
                }
              }

              const hasTargetMark = node.marks.some((mark) => {
                if (mark.type.name !== this.options.inlineThreadTypeName)
                  return false;
                const markThreadId = mark.attrs.id;
                return markThreadId === id;
              });

              if (hasTargetMark) {
                commands.setTextSelection(pos);
                if (scrollIntoView) commands.scrollIntoView();
                found = true;
                return false;
              }

              return true;
            });
          }

          return commands.run();
        },
      resolveThread:
        (threadId: string) =>
        ({ tr }) => {
          if (!this.options.provider || !threadId) return false;

          // this.options.provider.updateThread(threadId, {
          //   resolvedAt: new Date().toISOString(),
          // });

          tr.setMeta('threadUpdate', true);
          return true;
        },

      unresolveThread:
        (threadId: string) =>
        ({ tr }) => {
          if (!this.options.provider || !threadId) return false;

          // TODO: will be added in next release
          // this.options.provider.updateThread(threadId, {
          //   resolvedAt: null,
          // });

          tr.setMeta('threadUpdate', true);
          return true;
        },

      updateThread: (threadId: string, data: Record<string, unknown>) => () => {
        if (!this.options.provider || !threadId || !data) return false;

        // TODO: will be added in next release
        // this.options.provider.updateThread(threadId, { data });
        return true;
      },

      createComment:
        ({ threadId, content, data }) =>
        () => {
          if (!this.options.provider || !threadId || !content) return false;

          // TODO: will be added in next release
          // this.options.provider.addComment(threadId, { content, data });
          return true;
        },

      updateComment:
        ({ threadId, commentId, content, data }) =>
        () => {
          if (!this.options.provider || !threadId || !commentId) return false;
          if (!content && !data) return false;

          // TODO: will be added in next release
          // this.options.provider.updateComment(threadId, commentId, {
          //   content,
          //   data,
          // });
          return true;
        },

      removeComment:
        ({ threadId, commentId, deleteThread }) =>
        () => {
          if (!this.options.provider || !threadId || !commentId) return false;

          // TODO: will be added in next release
          // this.options.provider.deleteComment(threadId, commentId, {
          //   deleteThread,
          // });
          return true;
        },

      syncThreads: () => () => {
        syncThreadsWithProvider(this.editor, this.options);
        return true;
      },
    };
  },

  onTransaction() {
    if (!this.options.provider || !this.options.provider.isSynced) {
      return;
    }

    if (this.storage.debounceTimer) {
      clearTimeout(this.storage.debounceTimer);
    }

    this.storage.debounceTimer = window.setTimeout(() => {
      syncThreadsWithProvider(this.editor, this.options);
    }, 350);
  },

  onSelectionUpdate() {
    const { from, to, empty } = this.editor.state.selection;
    const threadIds = new Set<string>();

    this.editor.state.doc.nodesBetween(
      from,
      Math.min(from + 1, this.editor.state.doc.nodeSize - 2),
      (node) => {
        if (node.type.name === this.options.blockThreadTypeName) {
          const id = node.attrs.id;
          if (id) threadIds.add(id);
        }

        node.marks.forEach((mark) => {
          if (mark.type.name === this.options.inlineThreadTypeName) {
            const id = mark.attrs.id;
            if (id) threadIds.add(id);
          }
        });

        return true;
      }
    );

    if (this.storage.isPendingThread) {
      const isNodeSelection =
        this.editor.state.selection instanceof NodeSelection;
      const selection = empty ? null : { from, to, isNodeSelection };

      if (empty) {
        this.storage.isPendingThread = false;
      }

      const tr = this.editor.state.tr.setMeta('pendingThread', selection);
      this.editor.view.dispatch(tr);
    }

    this.storage.focusedThreads = Array.from(threadIds);
  },

  addProseMirrorPlugins() {
    if (!this.options.provider) {
      console.warn('Comments extension requires a provider');
      return [];
    }

    const plugins: Plugin[] = [];

    plugins.push(createDecorationPlugin(this.editor, this.name, this.options));
    plugins.push(createPendingDecorationPlugin(this.name));

    plugins.push(
      new Plugin({
        key: new PluginKey(`${this.name}__paste`),

        props: {
          handlePaste: (view, event, slice) => {
            let hasThreads = false;

            slice.content.descendants((node) => {
              if (node.type.name === this.options.blockThreadTypeName) {
                hasThreads = true;
              }
              if (
                node.marks.some(
                  (m) => m.type.name === this.options.inlineThreadTypeName
                )
              ) {
                hasThreads = true;
              }
            });

            if (!hasThreads) return false;

            const modifiedSlice = new Slice(
              removeThreadIds(slice.content, this.options),
              slice.openStart,
              slice.openEnd
            );

            const tr = view.state.tr.replaceSelection(modifiedSlice);
            view.dispatch(tr);

            return true;
          },
        },
      })
    );

    return plugins;
  },
});

function collectThreadIds(
  element: HTMLElement,
  topElement: HTMLElement
): string[] {
  const threadIds: string[] = [];
  const seen = new Set<string>();

  const addThreadId = (el: HTMLElement) => {
    const attrs = ['data-block-thread-id', 'data-inline-thread-id'];

    for (const attr of attrs) {
      const id = el.getAttribute(attr);
      if (id && !seen.has(id)) {
        seen.add(id);
        threadIds.push(id);
      }
    }
  };

  addThreadId(element);

  let current = element.parentElement;
  while (current && current !== topElement) {
    addThreadId(current);
    current = current.parentElement;
  }

  return threadIds;
}

function createDecorationPlugin(
  editor: Editor,
  extensionName: string,
  options: CommentsOptions
) {
  return new Plugin({
    key: new PluginKey(`${extensionName}__decorations`),

    state: {
      init() {
        return {
          decorations: DecorationSet.empty,
          hoveredThreads: [] as string[],
          selectedThreadId: undefined as string | undefined,
        };
      },

      apply(tr, pluginState) {
        let needsUpdate = tr.docChanged || tr.getMeta('threadUpdate');
        let newHoveredThreads = pluginState.hoveredThreads;
        let newSelectedThreadId = pluginState.selectedThreadId;

        const threadsToHover = tr.getMeta('threadMouseOver');
        if (threadsToHover !== undefined) {
          const hoveredSet = new Set(newHoveredThreads);
          threadsToHover.forEach((id: string) => hoveredSet.add(id));
          newHoveredThreads = Array.from(hoveredSet);
          needsUpdate = true;
        }

        const threadsToUnhover = tr.getMeta('threadMouseOut');
        if (threadsToUnhover !== undefined) {
          if (threadsToUnhover === null) {
            newHoveredThreads = [];
          } else {
            const removeSet = new Set(threadsToUnhover);
            newHoveredThreads = newHoveredThreads.filter(
              (id) => !removeSet.has(id)
            );
          }
          needsUpdate = true;
        }

        const threadSelect = tr.getMeta('threadSelect');
        if (threadSelect !== undefined) {
          newSelectedThreadId =
            threadSelect === false
              ? undefined
              : threadSelect || newSelectedThreadId;
          needsUpdate = true;
        }

        if (needsUpdate) {
          return {
            decorations: createDecorations(
              tr.doc,
              newHoveredThreads,
              newSelectedThreadId,
              options
            ),
            hoveredThreads: newHoveredThreads,
            selectedThreadId: newSelectedThreadId,
          };
        }

        if (tr.docChanged) {
          return {
            ...pluginState,
            decorations: pluginState.decorations.map(tr.mapping, tr.doc),
          };
        }

        return pluginState;
      },
    },

    props: {
      decorations(state) {
        return this.getState(state)?.decorations;
      },

      handleDOMEvents: {
        mouseover: (view, event) => {
          const target = event.target as HTMLElement;
          if (!target) return false;

          const threadElement = target.closest(
            '[data-block-thread-id], [data-inline-thread-id]'
          );
          if (!threadElement) return false;

          const threadIds = collectThreadIds(
            threadElement as HTMLElement,
            view.dom as HTMLElement
          );

          if (threadIds.length > 0) {
            view.dispatch(view.state.tr.setMeta('threadMouseOver', threadIds));
            return true;
          }

          return false;
        },

        mouseout: (view, event) => {
          const target = event.target as HTMLElement;
          if (!target) return false;

          const threadElement = target.closest(
            '[data-block-thread-id], [data-inline-thread-id]'
          );
          if (!threadElement) return false;

          const relatedTarget = event.relatedTarget as HTMLElement;
          const relatedThreadElement = relatedTarget?.closest(
            '[data-block-thread-id], [data-inline-thread-id]'
          );

          if (relatedThreadElement === threadElement) {
            return false;
          }

          const threadIds = collectThreadIds(
            threadElement as HTMLElement,
            view.dom as HTMLElement
          );

          if (threadIds.length > 0) {
            view.dispatch(view.state.tr.setMeta('threadMouseOut', threadIds));
            return true;
          }

          return false;
        },
      },

      handleClick: (view, pos) => {
        const { doc } = view.state;
        let node = null;
        const safetyPos = Math.min(doc.content.size, pos + 1);

        if (pos >= 0 && pos <= doc.content.size) {
          node =
            doc.nodeAt(pos) || doc.nodeAt(pos - 1) || doc.nodeAt(safetyPos);
        } else {
          return false;
        }

        if (!node) {
          view.dispatch(view.state.tr.setMeta('threadSelect', false));
          if (options.onClickThread) options.onClickThread(null);
          return false;
        }

        const inlineThreadMark = node.marks.find(
          (mark) => mark.type.name === options.inlineThreadTypeName
        );

        const $pos = doc.resolve(pos);
        let blockThreadId: string | null = null;

        if (node.type.name === options.blockThreadTypeName) {
          blockThreadId = node.attrs.id;
        } else {
          for (let depth = $pos.depth; depth > 0; depth--) {
            const ancestor = $pos.node(depth);
            if (ancestor.type.name === options.blockThreadTypeName) {
              blockThreadId = ancestor.attrs.id;
              break;
            }
          }
        }

        let threadId: string | null = null;

        if (inlineThreadMark) {
          threadId = inlineThreadMark.attrs.id;
        } else if (blockThreadId) {
          threadId = blockThreadId;
        }

        if (threadId) {
          view.dispatch(view.state.tr.setMeta('threadSelect', threadId));
          if (options.onClickThread) options.onClickThread(threadId);
          return true;
        }

        view.dispatch(view.state.tr.setMeta('threadSelect', false));
        if (options.onClickThread) options.onClickThread(null);
        return false;
      },
    },
  });
}

function createDecorations(
  doc: Node,
  hoveredThreadIds: string[],
  selectedThreadId: string | undefined,
  options: CommentsOptions
): DecorationSet {
  const decorations: Decoration[] = [];
  const threads = options.provider?.getThreads() || [];

  const hoveredSet = new Set(hoveredThreadIds);
  const threadMap = new Map(threads.map((t) => [t.id, t]));

  doc.descendants((node, pos) => {
    const isBlockThread = node.type.name === options.blockThreadTypeName;
    const hasInlineThread = node.marks.some(
      (mark) => mark.type.name === options.inlineThreadTypeName
    );

    if (!isBlockThread && !hasInlineThread) {
      return;
    }

    if (isBlockThread) {
      const threadId = node.attrs.id;

      if (!threadId) return;

      const thread = threadMap.get(threadId);
      const isResolved = !!thread?.resolvedAt;
      const isHovered = hoveredSet.has(threadId);
      const isSelected = selectedThreadId === threadId;

      const classes = [
        options.classes.thread,
        options.classes.block,
        isResolved ? options.classes.resolved : options.classes.unresolved,
      ];

      if (isHovered) classes.push(options.classes.hover);
      if (isSelected) classes.push(options.classes.selected);

      const decoration = Decoration.node(pos, pos + node.nodeSize, {
        class: classes.join(' '),
      });

      decorations.push(decoration);
    }

    if (hasInlineThread) {
      const threadMarks = node.marks.filter(
        (mark) => mark.type.name === options.inlineThreadTypeName
      );

      threadMarks.forEach((mark) => {
        const threadId = mark.attrs.id;

        if (!threadId) return;

        const thread = threadMap.get(threadId);
        const isResolved = !!thread?.resolvedAt;
        const isHovered = hoveredSet.has(threadId);
        const isSelected = selectedThreadId === threadId;

        const classes = [
          options.classes.thread,
          options.classes.inline,
          isResolved ? options.classes.resolved : options.classes.unresolved,
        ];

        if (isHovered) classes.push(options.classes.hover);
        if (isSelected) classes.push(options.classes.selected);

        const decoration = Decoration.inline(pos, pos + node.nodeSize, {
          class: classes.join(' '),
        });

        decorations.push(decoration);
      });
    }
  });

  return DecorationSet.create(doc, decorations);
}

function createPendingDecorationPlugin(extensionName: string) {
  return new Plugin({
    key: new PluginKey(`${extensionName}__pending-decoration`),

    state: {
      init() {
        return {
          isPending: false,
          decorations: DecorationSet.empty,
        };
      },

      apply(tr, pluginState) {
        const pendingThread = tr.getMeta('pendingThread');
        if (pendingThread) {
          let decoration;
          if (pendingThread.isNodeSelection) {
            decoration = Decoration.node(pendingThread.from, pendingThread.to, {
              class: 'pending-block-thread',
            });
          } else {
            decoration = Decoration.inline(
              pendingThread.from,
              pendingThread.to,
              { class: 'pending-inline-thread' }
            );
          }

          const decorations = DecorationSet.create(tr.doc, [decoration]);

          return {
            isPending: true,
            decorations: decorations,
          };
        }

        if (pendingThread === null) {
          return {
            isPending: false,
            decorations: DecorationSet.empty,
          };
        }

        return pluginState;
      },
    },

    props: {
      decorations(state) {
        return this.getState(state)?.decorations;
      },
    },
  });
}

function removeThreadIds(
  fragment: Fragment,
  options: CommentsOptions
): Fragment {
  const processed: Node[] = [];

  fragment.forEach((node) => {
    if (node.type.name === options.blockThreadTypeName) {
      const newAttrs = { ...node.attrs };
      delete newAttrs.id;

      const newNode = node.type.create(
        newAttrs,
        removeThreadIds(node.content, options),
        node.marks
      );
      processed.push(newNode);
    } else {
      const filteredMarks = node.marks.filter(
        (mark) => mark.type.name !== options.inlineThreadTypeName
      );

      if (node.isText) {
        processed.push(node.mark(filteredMarks));
      } else {
        const newNode = node.copy(removeThreadIds(node.content, options));
        processed.push(newNode.mark(filteredMarks));
      }
    }
  });

  return Fragment.fromArray(processed);
}
